/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { DocumentTypes, PDFTronTools, PDFTRON_DEFAULT_TOOL } from 'types';
import { getDocumentsLoaded } from 'store';
import store from 'store/store';
import { useSelector } from 'react-redux';
import { isInspectionUrl } from 'utils/location';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import selectTool from 'utils/selectTool/selectTool';

function useAnnotationTools() {
  const pdfDocManager = PDFManagerFactory.getPDFDocManager();

  const docsLoaded = useSelector(getDocumentsLoaded);

  useEffect(() => {
    if (docsLoaded) {
      pdfDocManager?.createGraphicZoneAnnotationTools();
      pdfDocManager?.createCropZoneAnnotationTools();
      pdfDocManager?.createZoneAnnotationTools();
      pdfDocManager?.createMarqueeZoneAnnotationTools();
      pdfDocManager?.createCrossoutAnnotationTools();
      pdfDocManager?.createMarkupZoneAnnotationTools();
      pdfDocManager?.createPolygonZoneAnnotationTools();
      pdfDocManager?.createResyncZoneAnnotationTool();
      pdfDocManager?.createMergeZoneAnnotationTools();
      pdfDocManager?.createColorPickerZoneAnnotationTools();
    }
  }, [docsLoaded]);

  useEffect(() => {
    if (!docsLoaded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isInspectionUrl()) return;

      const state = store.getState();
      const manualAlignMode = state.inspection.manualAlignMode;
      if (manualAlignMode.isActive) {
        e.stopPropagation();
        pdfDocManager?.exitManualAlignMode?.();
        useDocumentToastStateStore.getState().expireAllToast();
        return;
      }

      const selectedTool = state.inspection.selectedTool;
      const colorCheck = state.inspection.settings.colorCheck;
      if (selectedTool === PDFTronTools.COLOR_PICKER && colorCheck) {
        e.stopPropagation();
        selectTool(PDFTRON_DEFAULT_TOOL);
        useDocumentToastStateStore.getState().expireAllToast();
        return;
      }
    };

    const captureOptions = { capture: true } as AddEventListenerOptions;
    window.addEventListener('keydown', handleKeyDown, captureOptions);
    const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
    const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
    sourceWin?.addEventListener('keydown', handleKeyDown, captureOptions);
    if (targetWin && targetWin !== sourceWin) {
      targetWin.addEventListener('keydown', handleKeyDown, captureOptions);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, captureOptions);
      sourceWin?.removeEventListener('keydown', handleKeyDown, captureOptions);
      if (targetWin && targetWin !== sourceWin) {
        targetWin.removeEventListener('keydown', handleKeyDown, captureOptions);
      }
    };
  }, [docsLoaded, pdfDocManager]);

  return {};
}

export default useAnnotationTools;
